import { format } from "date-fns";

import { ClinicDermGroup, EMR, Lab, Location } from "pwapi";

import { request as __request } from "pwapi/core/request";
import { LabSettings } from "pwapi/custom/LabSettings";

type Params = {
  [key: string]: string | number;
};

export type FrontLoadRequestArgs = {
  updated_at__gte?: number;
};

const createFrontLoadRequest =
  <RecordType>(path: string, params?: Params) =>
  async (
    requestArgs: FrontLoadRequestArgs = {}
  ): Promise<Array<RecordType>> => {
    const paramsObj: Params = { ...params };
    if (requestArgs.updated_at__gte)
      paramsObj.updated_at__gte = encodeURIComponent(
        format(new Date(requestArgs.updated_at__gte), "yyyy-MM-dd HH:mm")
      );
    const paramsStr = Object.keys(paramsObj).reduce(
      (str, key, i) => `${str}${i > 0 ? "&" : "?"}${key}=${paramsObj[key]}`,
      ""
    );
    const result = await __request({
      method: "GET",
      path: `/api/front-load/${path}/${paramsStr}`,
    });
    return result.body;
  };

export type LabFrontLoadRequestArgs = FrontLoadRequestArgs & {
  lab_id: number;
};

const createLabFrontLoadRequest =
  <RecordType>(path: string, params?: Params) =>
  async (requestArgs: LabFrontLoadRequestArgs): Promise<Array<RecordType>> => {
    const paramsObj: Params = { ...params };
    if (requestArgs.updated_at__gte)
      paramsObj.updated_at__gte = encodeURIComponent(
        format(new Date(requestArgs.updated_at__gte), "yyyy-MM-dd HH:mm")
      );
    const paramsStr = Object.keys(paramsObj).reduce(
      (str, key, i) => `${str}${i > 0 ? "&" : "?"}${key}=${paramsObj[key]}`,
      ""
    );
    const result = await __request({
      method: "GET",
      path: `/api/labs/${requestArgs.lab_id}/front-load/${path}/${paramsStr}`,
    });
    return result.body;
  };

// This is stuff that should have been exported by OpenAPI but the generated types and requests aren't what we need yet.
// TODO: Get the backend to generate the proper OpenAPI schema and then replace this with auto-generated code in the pwapi library.

export type UserList = {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  userprofile: {
    user_type?: string; // TODO: Define actual possible values here
    title?: string | null;
    credentials?: string | null;
  };
};

export const frontLoadUsersRequest = createFrontLoadRequest<UserList>("users");

export type LabList = {
  id: number;
  name?: string;
  default_prefix_id?: number;
  default_active_assignment_id?: number;
  location?: Lab["location"];
  use_manual_insurance?: boolean;
  settings?: LabSettings;
};

export const frontLoadLabsRequest = createFrontLoadRequest<LabList>("labs");

export type AccessionPrefixList = {
  id: number;
  lab_id?: number;
  prefix?: string;
};

export const frontLoadAccessionPrefixesRequest =
  createLabFrontLoadRequest<AccessionPrefixList>("accession-prefixes");

export type LabStationList = {
  id: number;
  lab_id: number;
  type: string; // TODO: Define actual possible values
  cassette_printer_id: number;
  name?: string;
  clia_certificate_id?: number;
  description?: string;
};

export const frontLoadLabStationsRequest =
  createFrontLoadRequest<LabStationList>("lab-stations");

export type ClinicList = {
  id: number;
  name?: string;
  billing_types?: string[];
  emrs?: EMR[];
  tat_priority?: boolean;
  location?: Location;
  clinic_derm_group?: ClinicDermGroup;
  is_archived: boolean;
};

export const frontLoadClinicsRequest =
  createFrontLoadRequest<ClinicList>("clinics");

// These are our large-volume types which are serialized as JSON array of values

export type PatientListValues = [
  number, // id
  number, // clinic_id
  string, // first_name
  string // last_name
];

export type PatientList = {
  id: number;
  clinic_id?: number;
  first_name?: string;
  last_name?: string;
};

export const frontLoadPatientsRequest =
  createLabFrontLoadRequest<PatientListValues>("patients");

export type AccessionListValues = [
  number, // id
  string, // status // TODO: change type to only accept possible values
  number, // author_id
  number, // lab_id
  number, // clinic_id
  number // completed_at
];

export type AccessionList = {
  id: number;
  status: string; // TODO: Should only accept possible values
  author_id?: number;
  lab_id?: number;
  clinic_id?: number;
  completed_at?: number;
  patient: {
    first_name?: string;
    last_name?: string;
    middle_initial?: string;
    employer?: string;
    ssn?: string;
    sex?: string;
  };
  specimens: {
    name?: string;
    source?: string;
    biopsy_type?: string;
    clinical_history?: string;
  }[];
  billing_type?: string;
};

export const frontLoadAccessionsRequest =
  createLabFrontLoadRequest<AccessionListValues>("accessions");

export type CaseListValues = [
  number, // id
  string, // status // TODO: should only accept possible values
  number, // accessioning_lab_id
  number, // patient_id
  number, // ordering_doctor_id
  number, // active_assignment_id
  number, // screen_assignment_id
  number, // finalize_assignment_id
  string // accession
];

export type CaseList = {
  id: number;
  status: string; // TODO: should use actual values
  accessioning_lab_id?: number;
  patient_id?: number;
  ordering_doctor_id?: number;
  active_assignment_id?: number;
  screen_assignment_id?: number;
  finalize_assignment_id?: number;
  accession: string;
};

export const frontLoadCasesRequest =
  createLabFrontLoadRequest<CaseListValues>("cases");

export type WorkProcessListValues = [
  number, // id
  number, // completed_at
  number, // case_id
  number, // flag_id
  number // order_id
];

export type WorkProcessList = {
  id: number;
  completed_at?: number;
  case_id?: number;
  flag_id?: number;
  order_id?: number;
};

export const frontLoadWorkProcessesRequest =
  createLabFrontLoadRequest<WorkProcessListValues>("work-processes");

export type WorkProcessTaskListValues = [
  number, // id
  number, // work_id
  string, // type
  string, // state
  number // completed_at
];

export type WorkProcessTaskList = {
  id: number;
  work_id: number;
  type: string;
  state: string;
  completed_at?: number;
};

export const frontLoadWorkProcessTasksRequest =
  createLabFrontLoadRequest<WorkProcessTaskListValues>("work-process-tasks");

export type StainListValues = [
  id: number,
  category: string,
  name: string,
  abbreviation: string,
  is_archived: boolean,
  deliverable: string,
  deliverable_document_category: string
];

export type StainList = {
  id: number;
  category: string;
  name: string;
  abbreviation: string;
  is_archived: boolean;
  deliverable: string;
  deliverable_document_category: string | null | undefined;
};

export const frontLoadStainsRequest =
  createFrontLoadRequest<StainListValues>("stains");

export type LabStainListValues = [
  id: number,
  lab_id: number,
  stain_id: number,
  external_lab_stain_id: number,
  cutoff_time: string,
  turnaround_hours: number,
  label_override: string | null,
  antigen_retrieval_method_id: number | null,
  antibody_species_id: number | null,
  routine_stain_id: number | null,
  non_routine_stain_id: number | null,
  is_archived: boolean
];

export type LabStainList = {
  id: number;
  lab_id: number;
  stain_id: number;
  external_lab_stain_id?: number;
  cutoff_time?: string;
  turnaround_hours?: number;
  label_override?: string | null;
  antigen_retrieval_method_id?: number | null;
  antibody_species_id?: number | null;
  routine_stain_id?: number | null;
  non_routine_stain_id?: number | null;
  is_archived: boolean;
};

export const frontLoadLabStainsRequest =
  createFrontLoadRequest<LabStainListValues>("lab-stains");

export type ExternalLabListValues = [id: number, name: string];

export type ExternalLabList = {
  id: number;
  name: string;
};

export const frontLoadExternalLabsRequest =
  createFrontLoadRequest<ExternalLabListValues>("external-labs");

export type ExternalLabStainListValues = [
  id: number,
  external_lab_id: number,
  stain_id: number
];

export type ExternalLabStainList = {
  id: number;
  external_lab_id: number;
  stain_id: number;
};

export const frontLoadExternalLabStainsRequest =
  createFrontLoadRequest<ExternalLabStainListValues>("external-lab-stains");

export type LabPanelListValues = [
  id: number,
  lab_id: number,
  name: string,
  lab_stain_ids: number[]
];

export type LabPanelList = {
  id: number;
  lab_id: number;
  name: string;
  lab_stain_ids: number[];
};

export const frontLoadLabPanelsRequest =
  createFrontLoadRequest<LabPanelListValues>("lab-panels");

export type AntigenRetrievalMethodListValues = [
  id: number,
  name: string,
  abbreviation: string
];

export type AntigenRetrievalMethodList = {
  id: number;
  name: string;
  abbreviation: string;
};

export const frontLoadAntigenRetrievalMethodsRequest =
  createFrontLoadRequest<AntigenRetrievalMethodListValues>(
    "antigen-retrieval-methods"
  );

export type AntibodySpeciesListValues = [
  id: number,
  name: string,
  label?: string
];

export type AntibodySpeciesList = {
  id: number;
  name: string;
  label?: string;
};

export const frontLoadAntibodySpeciesRequest =
  createFrontLoadRequest<AntibodySpeciesListValues>("antibody-species");
